import { useLocation, Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import type { Permission } from '../../utils/permissions';
import { useAuthStore } from '../../stores/authStore';
import { useAddons } from '../../lib/addons';

/** Která stránka vyžaduje jaké oprávnění (první shoda podle začátku adresy; stačí kterékoli z uvedených). */
const RULES: Array<{ prefix: string; any: Permission[] }> = [
  { prefix: '/vehicles/new',   any: ['vehicles.create'] },
  { prefix: '/vehicles',       any: ['vehicles.view'] },
  { prefix: '/vykup',          any: ['vehicles.create'] },
  { prefix: '/sales',          any: ['vehicles.sell'] },
  { prefix: '/settings',       any: ['settings.view'] },
  { prefix: '/customers',      any: ['customers.view'] },
  { prefix: '/invoices/new',   any: ['invoices.create'] },
  { prefix: '/invoices',       any: ['invoices.view'] },
  { prefix: '/pokladna/kasa',  any: ['cashbook.manage'] },
  { prefix: '/pokladna/banka', any: ['cashbook.manage'] },
  { prefix: '/pokladna/export', any: ['cashbook.manage'] },
  { prefix: '/pokladna/prehled', any: ['cashbook.manage'] },
  { prefix: '/pokladna',       any: ['invoices.view', 'cashbook.manage'] },
  { prefix: '/advertising',    any: ['advertising.view'] },
  { prefix: '/statistics',     any: ['statistics.view'] },
  { prefix: '/investments',    any: ['investments.view'] },
  { prefix: '/inventory',      any: ['inventory.view'] },
  { prefix: '/service',        any: ['service.view'] },
  { prefix: '/aftersales',     any: ['aftersales.view'] },
];

/** Zobrazí potomky jen tehdy, když má role pro aktuální stránku oprávnění; jinak srozumitelnou hlášku. */
export default function RouteGuard({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { canAny } = usePermissions();
  const user = useAuthStore(s => s.user);
  const addons = useAddons();
  const boundFinance = addons.multiBranch && !!user?.branchId && /^\/pokladna\/(prehled|kasa|banka|export)(\/|$)/.test(pathname);
  const rule = /^\/vehicles\/[^/]+\/edit\/?$/.test(pathname)
    ? { prefix: pathname, any: ['vehicles.edit'] as Permission[] }
    : RULES.find(r => pathname === r.prefix || pathname.startsWith(r.prefix + '/'));
  if (boundFinance || (rule && !canAny(rule.any))) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center px-4">
        <ShieldAlert className="h-10 w-10 text-gray-300 mb-3" />
        <h2 className="text-base font-semibold text-gray-800">Na tuto stránku nemáte oprávnění</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-sm">Přístup nastavuje správce firmy v Nastavení → Uživatelé → Oprávnění rolí.</p>
        <Link to="/dashboard" className="mt-4 text-sm font-medium text-blue-600 hover:underline">Zpět na přehled</Link>
      </div>
    );
  }
  return <>{children}</>;
}
